import { apiClient, ApiResponse } from './apiClient';

export interface Permission {
  id: number;
  name: string;
  description: string;
  resource: string;
  action: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  Permissions: Permission[];
}

export interface RoleFilters {
  search?: string;
  status?: 'active' | 'inactive' | '';
}

export interface RoleListResponse {
  roles: Role[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  isActive?: boolean;
  permissions?: number[]; // Array of permission IDs
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
  permissions?: number[]; // Array of permission IDs
}

export interface RoleStats {
  total_roles: number;
  active_roles: number;
  inactive_roles: number;
}

export class RolesService {
  private baseUrl = '/admin/roles';

  // Get all roles
  async getRoles(filters?: RoleFilters): Promise<ApiResponse<Role[]>> {
    try {
      console.log('🔧 RolesService.getRoles called:', { filters });
      
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.status) params.append('status', filters.status);
      
      const url = params.toString() ? `${this.baseUrl}?${params}` : this.baseUrl;
      console.log('🔧 Making GET request to:', url);
      
      const response = await apiClient.get<Role[]>(url);
      
      console.log('🔧 RolesService.getRoles response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.getRoles error:', error);
      
      let errorMessage = 'Error fetching roles';
      
      if (error.response?.status === 500 || error.response?.status === 404) {
        errorMessage = 'Roles endpoint not available. Please ensure the backend API is running.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    }
  }

  // Get single role
  async getRole(id: number): Promise<ApiResponse<Role>> {
    try {
      console.log('🔧 RolesService.getRole called:', { id });
      
      const response = await apiClient.get<Role>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 RolesService.getRole response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.getRole error:', error);
      
      let errorMessage = 'Error fetching role';
      
      if (error.response?.status === 404) {
        errorMessage = 'Role not found';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Create role
  async createRole(data: CreateRoleRequest): Promise<ApiResponse<Role>> {
    try {
      console.log('🔧 RolesService.createRole called:', { data });
      console.log('🔧 Making POST request to:', this.baseUrl);
      
      // Try different field name formats that the backend might expect
      const apiPayload = {
        name: data.name,
        description: data.description,
        isActive: data.isActive !== undefined ? data.isActive : true,
        // Also try snake_case versions
        is_active: data.isActive !== undefined ? data.isActive : true,
        // Include permissions if provided (ensure it's an array)
        permissions: data.permissions || []
      };
      
      console.log('🔧 Request payload:', JSON.stringify(apiPayload, null, 2));
      
      const response = await apiClient.post<Role>(this.baseUrl, apiPayload);
      
      console.log('🔧 RolesService.createRole response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.createRole error:', error);
      console.error('🔧 Error response status:', error.response?.status);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      let errorMessage = 'Error creating role';
      
      if (error.response?.status === 422) {
        console.error('🔧 422 Validation Error Details:');
        console.error('🔧 - Status:', error.response.data?.status);
        console.error('🔧 - Message:', error.response.data?.message);
        console.error('🔧 - Errors:', error.response.data?.errors);
        
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            console.error('🔧 Processing error:', err);
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            if (err.field && err.message) return `${err.field}: ${err.message}`;
            return err.msg || err.message || JSON.stringify(err);
          });
          errorMessage = validationErrors.join(', ');
          console.error('🔧 Final validation errors:', validationErrors);
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'Role name already exists';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Update role
  async updateRole(id: number, data: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    try {
      console.log('🔧 RolesService.updateRole called:', { id, data });
      
      const response = await apiClient.put<Role>(`${this.baseUrl}/${id}`, data);
      
      console.log('🔧 RolesService.updateRole response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.updateRole error:', error);
      
      let errorMessage = 'Error updating role';
      
      if (error.response?.status === 422) {
        errorMessage = 'Validation error - Please check your input data';
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const validationErrors = error.response.data.errors.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg && err.path) return `${err.path}: ${err.msg}`;
            return err.msg || err.message || 'Validation error';
          });
          errorMessage = validationErrors.join(', ');
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Role not found';
      } else if (error.response?.status === 409) {
        errorMessage = 'Role name already exists';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Delete role
  async deleteRole(id: number): Promise<ApiResponse<any>> {
    try {
      console.log('🔧 RolesService.deleteRole called:', { id });
      
      const response = await apiClient.delete<any>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 RolesService.deleteRole response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.deleteRole error:', error);
      
      let errorMessage = 'Error deleting role';
      
      if (error.response?.status === 404) {
        errorMessage = 'Role not found';
      } else if (error.response?.status === 409) {
        errorMessage = 'Cannot delete role - it is being used by users';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Toggle role status
  async toggleRoleStatus(id: number): Promise<ApiResponse<Role>> {
    try {
      console.log('🔧 RolesService.toggleRoleStatus called:', { id });
      
      const response = await apiClient.patch<Role>(`${this.baseUrl}/${id}/toggle-status`);
      
      console.log('🔧 RolesService.toggleRoleStatus response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 RolesService.toggleRoleStatus error:', error);
      
      let errorMessage = 'Error toggling role status';
      
      if (error.response?.status === 404) {
        errorMessage = 'Role not found';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage
      };
    }
  }

  // Calculate stats from roles data
  calculateStats(roles: Role[]): RoleStats {
    return {
      total_roles: roles.length,
      active_roles: roles.filter(role => role.isActive).length,
      inactive_roles: roles.filter(role => !role.isActive).length,
    };
  }
}

export const rolesService = new RolesService();
